/**
 * The browser downloader's engine: fetch a chunk's files, zip them, hand the zip to the
 * browser, tell the server what landed. See todo/browser-downloader.md.
 *
 * Deliberately has no DOM dependencies beyond the anchor click, so the queue can later move
 * into a Web Worker without restructuring — background tabs throttle timers hard, and these
 * downloads run for tens of minutes.
 *
 * The old Lambda downloader is untouched; this only runs for members with
 * `uses_browser_downloader` set.
 */
import { downloadZip } from "client-zip";
import { api } from "../../api";
import {
  emptyTally,
  prefetchFiles,
  saveZip,
  FINAL_ZIP_RELEASE_MS,
  type DownloadFile,
  type FetchTally,
} from "../zipStream";

export type { DownloadFile };

export interface ChunkProgress {
  /** Files fetched so far in this chunk. */
  filesDone: number;
  /** Files in this chunk. */
  filesTotal: number;
  /** Artworks reported delivered across the whole job. */
  artworksDone: number;
  /** Artworks in the whole job. */
  artworksTotal: number;
  /** 1-based chunk being worked on. */
  chunkIndex: number;
  chunkCount: number;
  /** Files that could not be fetched after every retry. */
  failures: string[];
}

/**
 * One zip's worth of work, as the server planned it.
 *
 * `partIndex` and `filename` come from the server rather than the loop counter: they are the
 * identity the reseller sees ("part 3", `…-part3.zip`) and the key a later re-download is
 * requested by, so a resumed job — which skips finished parts and therefore has a different
 * loop counter — must not renumber them.
 */
export interface DownloadPart {
  partIndex: number;
  artworkIds: number[];
  filename: string;
  /**
   * This zip holds the artists' marketing material, not artwork.
   *
   * ⚠️ It carries no `artworkIds`, so every artwork-shaped rule has to skip it: it reports
   * nothing delivered, registers no licence and moves no progress. The flag comes from the
   * server's stored plan rather than being inferred from an empty id list, which would also
   * be true of an artwork part whose contents were all delivered already.
   */
  isMarketing?: boolean;
}

export interface EngineCallbacks {
  onProgress: (p: ChunkProgress) => void;
  onDone: (summary: { artworksDone: number; artworksTotal: number; failures: string[] }) => void;
  onError: (message: string) => void;
}

/** An artwork counts as delivered when its ORIGINAL landed — a failed mockup must not
 *  withhold the artwork, because the original is what carries the licence. */
const originLanded = (f: DownloadFile): boolean => f.target === `${f.artworkId}.jpg`;

/**
 * Run a browser download to completion (or until aborted).
 *
 * One zip per chunk. A partial zip is corrupt — the central directory is written last — so a
 * chunk is only reported once its zip has actually been handed to the browser. Reporting
 * earlier would mark artworks delivered that the reseller never received; reporting late at
 * worst re-downloads one chunk.
 */
export async function runBrowserDownload(
  args: {
    downloadId: string;
    parts: DownloadPart[];
    artworksTotal: number;
    artworksAlreadyDone: number;
    signal: AbortSignal;
  },
  cb: EngineCallbacks
): Promise<void> {
  const { downloadId, parts, signal } = args;
  let artworksDone = args.artworksAlreadyDone;
  const failures: string[] = [];
  /** Object URL of the zip handed over most recently; released when the next one lands. */
  let previousZipUrl: string | null = null;

  try {
    for (let c = 0; c < parts.length; c++) {
      if (signal.aborted) break;
      const part = parts[c]!;

      const manifest = await api.downloadCart.browserChunkManifest({
        downloadId,
        artworkIds: part.artworkIds,
        // The server reads `is_marketing` off this part; it never trusts a client flag.
        partIndex: part.partIndex,
      });
      // Everything in this chunk was already delivered (a resumed job, or a repeated report).
      if (manifest.files.length === 0) continue;

      const tally: FetchTally = emptyTally();
      const report = (): void =>
        cb.onProgress({
          filesDone: tally.done,
          filesTotal: manifest.files.length,
          // Artworks confirmed by the server PLUS those already landed in the chunk being
          // fetched. Counting only confirmed chunks left the display on "0 of 4" for the
          // whole of a single-chunk download: the one report arrives as the overlay closes,
          // so the finished count is never on screen. Same mistake the progress bar made.
          artworksDone: artworksDone + tally.delivered.size,
          artworksTotal: args.artworksTotal,
          chunkIndex: c + 1,
          chunkCount: parts.length,
          failures: [...failures, ...tally.failures],
        });
      report();

      // The zip is built as the files arrive rather than after they have all landed, so only
      // the archive itself and the small look-ahead window are ever in memory.
      const zip = await downloadZip(
        prefetchFiles(manifest.files, signal, tally, report, originLanded)
      ).blob();
      failures.push(...tally.failures);
      if (signal.aborted) break;
      if (tally.zipped === 0) continue;

      const url = saveZip(zip, part.filename);
      if (previousZipUrl) URL.revokeObjectURL(previousZipUrl);
      previousZipUrl = url;

      // A marketing zip has landed; mark the part so a resume does not hand it over twice.
      // Deliberately NOT `reportBrowserChunk`: that path registers licences and decides
      // whether the job is finished, neither of which is true of marketing material.
      if (part.isMarketing === true) {
        await api.downloadCart.reportBrowserMarketingPart({
          downloadId,
          partIndex: part.partIndex,
        });
        report();
        continue;
      }

      const delivered = [...tally.delivered];
      if (delivered.length > 0) {
        const res = await api.downloadCart.reportBrowserChunk({
          downloadId,
          artworkIds: delivered,
          // The stored part, so the downloads modal can show which zips actually landed and
          // offer the others for re-download.
          partIndex: part.partIndex,
        });
        artworksDone = res.completed;
        // These are now counted in `artworksDone`; leaving them in the tally would count the
        // chunk twice on the report that follows.
        tally.delivered.clear();
      }
      report();
    }

    cb.onDone({ artworksDone, artworksTotal: args.artworksTotal, failures });
  } catch (e) {
    if (signal.aborted) {
      cb.onDone({ artworksDone, artworksTotal: args.artworksTotal, failures });
      return;
    }
    cb.onError(e instanceof Error ? e.message : "Download failed");
  } finally {
    // The last zip has no successor to release it, so it gets a grace period instead — long
    // enough for the browser to have taken the bytes, short enough not to strand an archive
    // in memory for the rest of the session.
    if (previousZipUrl) {
      const url = previousZipUrl;
      previousZipUrl = null;
      setTimeout(() => URL.revokeObjectURL(url), FINAL_ZIP_RELEASE_MS);
    }
  }
}

/**
 * Rebuild ONE already-delivered zip and hand it to the browser again.
 *
 * The browser downloader keeps no copy of what it built — the zip goes straight to the OS —
 * so "download part 3 again" means fetching part 3's files and re-assembling them. That is
 * the whole feature: a zip that failed to save can be replaced without re-running the job
 * and without the other parts arriving a second time.
 *
 * It reports NOTHING to the server. The files were already delivered and already counted;
 * `reportBrowserChunk` is idempotent so a report would be harmless today, but calling it
 * here is how a rebuild would start moving job progress the day that changes — which is the
 * failure the Lambda downloader shipped with (progress reaching 142%,
 * todo/downloader-hardening.md §2).
 */
export async function rebuildDownloadPart(
  args: { files: DownloadFile[]; filename: string; signal: AbortSignal },
  cb: { onProgress: (done: number, total: number) => void; onDone: (failures: string[]) => void }
): Promise<void> {
  const tally: FetchTally = emptyTally();
  const report = (): void => cb.onProgress(tally.done, args.files.length);
  report();

  const zip = await downloadZip(prefetchFiles(args.files, args.signal, tally, report)).blob();
  if (args.signal.aborted) {
    cb.onDone(tally.failures);
    return;
  }

  const url = saveZip(zip, args.filename);
  setTimeout(() => URL.revokeObjectURL(url), FINAL_ZIP_RELEASE_MS);
  cb.onDone(tally.failures);
}
